import { api } from './api';
import { PAGINATION } from '../constants';

export interface CreateArchiveRequest {
  asset: string;
  documentType: string;
  documentName: string;
  activity: string;
  activityDate: Date;
  documentUrl: string;
  services: string;
  documents: string;
}

export interface ArchiveDocument {
  _id: string;
  id: string; // Add this for convenience
  asset: {
    _id: string;
    name: string;
    slug: string;
    description: string;
    serialNo: string;
    assetDocumentation: string;
    location: string;
    type: string;
    category: string;
    purchaseDate: string;
    purchasePrice: number;
    condition: string;
    status: string;
    warranteeExpiryDate: string;
    owner: {
      _id: string;
      name: string;
      email: string;
      role: string;
      status: string;
      department: string;
      isClient: boolean;
      address: string;
      website: string;
      contactPerson: string;
      phone: string;
      createdAt: string;
      updatedAt: string;
    };
    createdAt: string;
    updatedAt: string;
  };
  documentType: string;
  documentName: string;
  activity: string;
  activityDate: string;
  documentUrl: string;
  documents: string;
  services: string;
  maintenanceType?: string;
  assetName?: string;
  clientName?: string;
  createdAt: string;
  updatedAt: string;
  isDeleted?: boolean;
  deletedAt?: string | null;
  deletedBy?: string | { _id: string; name?: string } | null;
  versions?: ArchiveVersionEntry[];
}

export interface ArchiveVersionEntry {
  documentUrl: string;
  documentName: string;
  documentType: string;
  activity: string;
  activityDate: string;
  services?: string;
  documents?: string;
  replacedAt: string;
  replacedBy: string | { _id: string; name?: string };
  eventType: 'edit' | 'delete' | 'restore';
}

export interface ClientArchiveSummary {
  clientId: string;
  clientName: string;
  documentCount: number;
}

export interface ArchiveListResponse {
  result: ArchiveDocument[];
  totalCount: number;
  currentPage: number;
  totalPages: number;
  previousPage: number | null;
  nextPage: number | null;
}

export interface ApiResponse {
  message: string;
  data: ArchiveListResponse;
  error: boolean;
}

export interface ArchiveFilters {
  page?: number;
  limit?: number;
  search?: string;
  maintenanceType?: string;
  asset?: string;
  client?: string;
  dateFrom?: string;
  dateTo?: string;
  includeDeleted?: boolean;
}

class MaintenanceArchiveService {
  async createArchive(data: CreateArchiveRequest): Promise<ArchiveDocument> {
    const response = await api.post('/asset-archive', data);
    return response.data.data || response.data;
  }

  async getArchives(filters?: ArchiveFilters): Promise<ArchiveListResponse> {
    const params = new URLSearchParams();
    
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.search) params.append('search', filters.search);
    if (filters?.maintenanceType) params.append('maintenanceType', filters.maintenanceType);
    if (filters?.asset) params.append('asset', filters.asset);
    if (filters?.client) params.append('client', filters.client);
    if (filters?.dateFrom) params.append('dateFrom', filters.dateFrom);
    if (filters?.dateTo) params.append('dateTo', filters.dateTo);
    if (filters?.includeDeleted) params.append('includeDeleted', 'true');

    const response = await api.get(`/asset-archive?${params.toString()}`);
    return response.data.data;
  }

  async getClientArchiveSummary(): Promise<ClientArchiveSummary[]> {
    const response = await this.getArchives({ limit: PAGINATION.FETCH_ALL_LIMIT }); // Get all records to summarize
    const clientMap = new Map<string, ClientArchiveSummary>();

    response.result.forEach(archive => {
      const clientId = archive.asset.owner._id;
      const clientName = archive.asset.owner.name;
      
      if (clientMap.has(clientId)) {
        const existing = clientMap.get(clientId)!;
        existing.documentCount += 1;
      } else {
        clientMap.set(clientId, {
          clientId,
          clientName,
          documentCount: 1
        });
      }
    });

    return Array.from(clientMap.values());
  }

  async getArchiveById(id: string): Promise<ArchiveDocument> {
    const response = await api.get(`/asset-archive/${id}`);
    const data = response.data.data || response.data;
    
    // Normalize the response to include convenience properties
    const normalizedData: ArchiveDocument = {
      ...data,
      id: data._id || data.id,
      assetName: data.asset?.name,
      clientName: data.asset?.owner?.name
    };
    
    return normalizedData;
  }

  async updateArchive(id: string, data: Partial<CreateArchiveRequest>): Promise<ArchiveDocument> {
    const response = await api.put(`/asset-archive/${id}`, data);
    return response.data.data || response.data;
  }

  async deleteArchive(id: string): Promise<void> {
    await api.delete(`/asset-archive/${id}`);
  }

  async restoreArchive(id: string): Promise<ArchiveDocument> {
    const response = await api.post(`/asset-archive/${id}/restore`);
    return response.data.data || response.data;
  }

  async downloadDocument(id: string): Promise<Blob> {
    const response = await api.get(`/asset-archive/${id}/download`, {
      responseType: 'blob'
    });
    return response.data;
  }
}

const maintenanceArchiveService = new MaintenanceArchiveService();
export default maintenanceArchiveService;